import * as React from "react";
import { ActivityIndicator, View } from "react-native";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";

import { colors } from "~/colors";
import { useHonoClient } from "~/hooks/HonoContext";
import { displayNameToSlug, FieldSync } from "~/hooks/useFieldSync";
import {
  createAccountSlugValidator,
  displayName,
  slug as slugSchema,
} from "~/lib/validation";
import { useGlobalStore } from "~/stores/global";
import { Button } from "~/ui/Button";
import { Form } from "~/ui/Form";
import { TextInput } from "~/ui/TextInput";

export function CreateTeamForm(props: {
  inBottomSheet?: boolean;
  onDismiss: () => void;
  onSuccess: (slug: string) => void;
}) {
  const { inBottomSheet, onDismiss, onSuccess = () => undefined } = props;
  const storeContext = useGlobalStore();
  const { hc } = useHonoClient();
  const schema = React.useMemo(() => {
    const slugValidator = createAccountSlugValidator({ apiClient: hc });
    return z.object({
      displayName,
      slug: slugSchema.refine(slugValidator, {
        message: "This URL is not available",
      }),
    });
  }, []);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: "",
      slug: "",
    },
  });
  const {
    control,
    handleSubmit,
    setFocus,
    formState: { isSubmitting, isValidating },
    setValue,
  } = form;

  async function onSubmit(data: z.infer<typeof schema>) {
    const res = await hc.accounts.$post({
      json: {
        displayName: data.displayName,
        type: "ORGANIZATION",
        slug: data.slug,
      },
    });
    if (res.ok) {
      const { data: account } = await res.json();
      await storeContext.refreshStore();
      onSuccess(account.slug);
    }
  }
  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <View className="w-full gap-4">
          <Controller
            control={control}
            name="displayName"
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Team name"
                autoFocus
                inBottomSheet={inBottomSheet}
                onChangeText={field.onChange}
                value={field.value ?? ""}
                errorMessage={error?.message}
                enterKeyHint="next"
                autoCorrect={false}
                onSubmitEditing={() => {
                  setFocus("slug");
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="slug"
            render={({ field, fieldState: { error } }) => (
              <View className="relative">
                <TextInput
                  {...field}
                  label="Team Slug"
                  onChangeText={(text) => {
                    setValue("slug", text.toLowerCase(), {
                      shouldValidate: true,
                    });
                  }}
                  inBottomSheet={inBottomSheet}
                  value={field.value ?? ""}
                  autoCapitalize="none"
                  errorMessage={error?.message}
                  autoComplete="off"
                  enterKeyHint="done"
                  data-1p-ignore
                />
              </View>
            )}
          />
          <FieldSync
            source="displayName"
            destination="slug"
            transformFn={displayNameToSlug}
          />
          <View>
            <Button
              variant="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting || isValidating}
              role="button"
              isLoading={isValidating || isSubmitting}
            >
              Create Team
            </Button>
            <Button
              variant="secondary"
              onPress={onDismiss}
              disabled={isSubmitting}
              role="button"
            >
              Cancel
            </Button>
          </View>
        </View>
      </Form>
    </FormProvider>
  );
}
